import React from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, Zap } from "lucide-react";
import { VLYT } from "@/constants/testIds/vlyt";
import WaitlistModal from "@/components/vlyt/WaitlistModal";
import ThemeToggle from "@/components/vlyt/ThemeToggle";

const LOGO_URL =
  "https://customer-assets.emergentagent.com/job_vlyt-launch/artifacts/3733o5po_02ebb175-0a71-49d5-93b5-137f92b27689.jpeg";

const NAV = [
  { to: "/", label: "Home", id: VLYT.nav.home, end: true },
  { to: "/about", label: "About", id: VLYT.nav.about },
  { to: "/about/miesindou", label: "Founder", id: VLYT.nav.founder },
  { to: "/support", label: "Support", id: VLYT.nav.support },
  { to: "/app", label: "Get the app", id: VLYT.nav.app },
];

export default function Nav() {
  const [scrolled, setScrolled] = React.useState(false);
  const [open, setOpen] = React.useState(false);
  const [waitlistOpen, setWaitlistOpen] = React.useState(false);
  const location = useLocation();

  React.useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 32);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  React.useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  return (
    <>
      <header
        className={`fixed top-0 inset-x-0 z-50 transition-colors duration-300 ${
          scrolled
            ? "backdrop-blur-xl border-b border-[color:var(--vlyt-border)]"
            : "bg-transparent"
        }`}
        style={scrolled ? { background: "color-mix(in srgb, var(--vlyt-surface-1) 72%, transparent)" } : undefined}
      >
        <div className="mx-auto max-w-[1440px] px-5 md:px-10 h-[68px] flex items-center justify-between">
          <Link to="/" data-testid={VLYT.nav.logo} className="flex items-center gap-3 group">
            <img
              src={LOGO_URL}
              alt="Vlyt"
              className="h-9 w-9 rounded-lg object-cover ring-1 ring-[color:var(--vlyt-border)] group-hover:ring-[color:var(--vlyt-lime)]"
            />
            <span className="font-display text-[18px] tracking-tight">
              V<span className="text-lime">L</span>YT
            </span>
          </Link>

          <nav className="hidden md:flex items-center gap-1">
            {NAV.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.end}
                data-testid={item.id}
                className={({ isActive }) =>
                  `px-4 py-2 rounded-full text-[14px] transition ${
                    isActive
                      ? "text-lime bg-[rgba(191,255,0,0.06)]"
                      : "text-[color:var(--vlyt-text)] hover:text-lime hover:bg-white/[0.03]"
                  }`
                }
              >
                {item.label}
              </NavLink>
            ))}
          </nav>

          <div className="hidden md:flex items-center gap-3">
            <ThemeToggle />
            <button
              type="button"
              onClick={() => setWaitlistOpen(true)}
              data-testid={VLYT.nav.installCta}
              className="btn-lime"
            >
              <Zap size={16} strokeWidth={2.6} /> Join waitlist
            </button>
          </div>

          <div className="md:hidden flex items-center gap-2">
            <ThemeToggle compact />
            <button
              type="button"
              className="inline-flex items-center justify-center h-10 w-10 rounded-full border border-[color:var(--vlyt-border)]"
              data-testid={VLYT.nav.hamburger}
              onClick={() => setOpen(true)}
              aria-label="Open menu"
            >
              <Menu size={20} />
            </button>
          </div>
        </div>
      </header>

      {open && (
        <div
          className="fixed inset-0 z-[60] backdrop-blur-xl md:hidden"
          style={{ background: "color-mix(in srgb, var(--vlyt-base) 96%, transparent)" }}
          data-testid={VLYT.nav.mobileOverlay}
        >
          <div className="flex items-center justify-between px-5 h-[68px] border-b border-[color:var(--vlyt-border)]">
            <span className="font-display text-lg">Menu</span>
            <button
              type="button"
              onClick={() => setOpen(false)}
              data-testid={VLYT.nav.mobileClose}
              aria-label="Close menu"
              className="h-10 w-10 rounded-full border border-[color:var(--vlyt-border)] flex items-center justify-center"
            >
              <X size={20} />
            </button>
          </div>
          <div className="px-6 py-8 flex flex-col gap-2">
            {NAV.map((item) => (
              <Link
                key={item.to}
                to={item.to}
                data-testid={`${item.id}-mobile`}
                className="text-h3 py-3 border-b border-[color:var(--vlyt-border)] hover:text-lime"
              >
                {item.label}
              </Link>
            ))}
            <button
              type="button"
              onClick={() => {
                setWaitlistOpen(true);
                setOpen(false);
              }}
              className="btn-lime mt-6 self-start"
              data-testid={`${VLYT.nav.installCta}-mobile`}
            >
              <Zap size={16} strokeWidth={2.6} /> Join waitlist
            </button>
          </div>
        </div>
      )}

      <WaitlistModal open={waitlistOpen} onOpenChange={setWaitlistOpen} source="nav" />
    </>
  );
}
